import React, { useState } from 'react';
import { Plus, Trash2, Check } from 'lucide-react';
import { usePatternStudio } from '../context/PatternStudioContext';

const CATEGORY_GROUPS = [
  {
    label: 'Essential',
    codes: ['MR', 'sc', 'sl st', 'ch', 'st(s)', 'FO'],
  },
  {
    label: 'Increases & Decreases',
    codes: ['inc', 'dec'],
  },
  {
    label: 'Other Stitches',
    codes: ['dc', 'hdc', 'BLO', 'FLO', 'pm'],
  },
];

function AbbrevRow({ abbr, onToggle, onUpdate, onRemove }) {
  const [editing, setEditing] = useState(false);

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
        abbr.selected
          ? 'border-stone-300 bg-stone-50'
          : 'border-gray-100 bg-white hover:border-stone-200'
      }`}
    >
      {/* Checkbox */}
      <button
        onClick={() => onToggle(abbr.id)}
        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
          abbr.selected ? 'border-stone-500 bg-stone-500' : 'border-gray-300 bg-white'
        }`}
      >
        {abbr.selected && <Check size={11} className="text-white" strokeWidth={3} />}
      </button>

      {/* Code */}
      {abbr.isCustom && editing ? (
        <input
          autoFocus
          value={abbr.code}
          onChange={e => onUpdate(abbr.id, { code: e.target.value })}
          onBlur={() => setEditing(false)}
          className="font-mono font-bold text-sm text-gray-800 w-20 border-b border-stone-400 bg-transparent outline-none"
        />
      ) : (
        <span
          className="font-mono font-bold text-sm text-gray-800 w-20 flex-shrink-0 cursor-pointer"
          onClick={() => abbr.isCustom && setEditing(true)}
        >
          {abbr.code || <span className="text-gray-400 font-normal italic">code</span>}
        </span>
      )}

      {/* Definition */}
      {abbr.isCustom ? (
        <input
          value={abbr.definition}
          onChange={e => onUpdate(abbr.id, { definition: e.target.value })}
          placeholder="Definition…"
          className="flex-1 text-sm text-gray-700 border-b border-gray-200 bg-transparent outline-none focus:border-stone-400 placeholder-gray-400"
        />
      ) : (
        <span className="flex-1 text-sm text-gray-600">{abbr.definition}</span>
      )}

      {abbr.isCustom && (
        <button
          onClick={() => onRemove(abbr.id)}
          className="p-1 text-gray-300 hover:text-red-500 flex-shrink-0"
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}

export default function Step3Abbreviations() {
  const { state, dispatch } = usePatternStudio();
  const abbreviations = state.pattern.abbreviations || [];

  const selected = abbreviations.filter(a => a.selected);
  const allSelected = abbreviations.length > 0 && selected.length === abbreviations.length;

  function toggleAll() {
    abbreviations.forEach(a => {
      if (allSelected !== a.selected) {
        dispatch({ type: 'TOGGLE_ABBREVIATION', id: a.id });
      }
    });
    if (!allSelected) {
      abbreviations.forEach(a => {
        if (!a.selected) dispatch({ type: 'TOGGLE_ABBREVIATION', id: a.id });
      });
    } else {
      abbreviations.forEach(a => {
        if (a.selected) dispatch({ type: 'TOGGLE_ABBREVIATION', id: a.id });
      });
    }
  }

  function selectGroup(codes) {
    codes.forEach(code => {
      const abbr = abbreviations.find(a => a.code === code);
      if (abbr && !abbr.selected) dispatch({ type: 'TOGGLE_ABBREVIATION', id: abbr.id });
    });
  }

  return (
    <div className="p-6 max-w-xl">
      <h2 className="text-lg font-bold text-gray-900 mb-1">Abbreviations</h2>
      <p className="text-sm text-gray-500 mb-2">
        Select which abbreviations appear in the pattern. Custom ones you add show up on the PDF.
      </p>

      {/* Stats + Controls */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-gray-500">
          {selected.length} of {abbreviations.length} selected
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => abbreviations.filter(a => !a.selected).forEach(a => dispatch({ type: 'TOGGLE_ABBREVIATION', id: a.id }))}
            className="text-xs text-stone-600 hover:underline"
          >
            Select all
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => abbreviations.filter(a => a.selected).forEach(a => dispatch({ type: 'TOGGLE_ABBREVIATION', id: a.id }))}
            className="text-xs text-stone-600 hover:underline"
          >
            Clear all
          </button>
        </div>
      </div>

      {/* Group shortcuts */}
      <div className="flex flex-wrap gap-2 mb-4">
        {CATEGORY_GROUPS.map(g => (
          <button
            key={g.label}
            onClick={() => selectGroup(g.codes)}
            className="px-3 py-1.5 rounded-lg border border-stone-200 text-xs text-gray-600 hover:bg-stone-50 hover:border-stone-400 transition-all"
          >
            + {g.label}
          </button>
        ))}
      </div>

      {/* Abbreviation list */}
      <div className="space-y-1.5">
        {/* Preset abbreviations */}
        {abbreviations.filter(a => !a.isCustom).map(abbr => (
          <AbbrevRow
            key={abbr.id}
            abbr={abbr}
            onToggle={id => dispatch({ type: 'TOGGLE_ABBREVIATION', id })}
            onUpdate={(id, changes) => dispatch({ type: 'UPDATE_ABBREVIATION', id, changes })}
            onRemove={id => dispatch({ type: 'REMOVE_ABBREVIATION', id })}
          />
        ))}

        {/* Separator if custom exist */}
        {abbreviations.some(a => a.isCustom) && (
          <div className="flex items-center gap-3 py-2">
            <div className="flex-1 border-t border-stone-200" />
            <span className="text-xs text-gray-400 font-medium">Custom</span>
            <div className="flex-1 border-t border-stone-200" />
          </div>
        )}

        {/* Custom abbreviations */}
        {abbreviations.filter(a => a.isCustom).map(abbr => (
          <AbbrevRow
            key={abbr.id}
            abbr={abbr}
            onToggle={id => dispatch({ type: 'TOGGLE_ABBREVIATION', id })}
            onUpdate={(id, changes) => dispatch({ type: 'UPDATE_ABBREVIATION', id, changes })}
            onRemove={id => dispatch({ type: 'REMOVE_ABBREVIATION', id })}
          />
        ))}
      </div>

      {/* Add custom */}
      <button
        onClick={() => dispatch({ type: 'ADD_CUSTOM_ABBREVIATION' })}
        className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-stone-400 hover:text-stone-700 transition-all w-full justify-center"
      >
        <Plus size={14} /> Add Custom Abbreviation
      </button>

      {/* Preview of selected */}
      {selected.length > 0 && (
        <div className="mt-6 p-4 bg-stone-50 rounded-xl border border-stone-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Will appear in PDF:</p>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5">
            {selected.map(a => (
              <span key={a.id} className="text-xs text-gray-700">
                <span className="font-mono font-bold">{a.code}</span>
                {' — '}
                {a.definition}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
